import { initializeApp, type FirebaseApp, type FirebaseOptions } from "firebase/app";
import { connectAuthEmulator, getAuth, type Auth } from "firebase/auth";
import { connectFirestoreEmulator, getFirestore, type Firestore } from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions, type Functions } from "firebase/functions";

/** Same region as the Cloud Functions (functions/src/config.ts). */
const FUNCTIONS_REGION = "southamerica-east1";

export const usingEmulators = import.meta.env.VITE_USE_EMULATORS === "true";

export interface Services {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  functions: Functions;
}

let services: Services | null = null;

/**
 * Web app config, in order: variables baked in at build time; a placeholder for the emulators (which
 * ignore the API key); or Firebase Hosting's reserved /__/firebase/init.json, so a production build
 * needs no secrets or config files.
 */
async function loadOptions(): Promise<FirebaseOptions> {
  const env = import.meta.env;
  const projectId = (env.VITE_FIREBASE_PROJECT_ID as string | undefined) ?? "acordai-app-c4fa3";
  if (env.VITE_FIREBASE_API_KEY) {
    return {
      apiKey: env.VITE_FIREBASE_API_KEY as string,
      appId: env.VITE_FIREBASE_APP_ID as string | undefined,
      authDomain: (env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined) ?? `${projectId}.firebaseapp.com`,
      projectId,
    };
  }
  if (usingEmulators) return { apiKey: "emulator-api-key", authDomain: "127.0.0.1", projectId };

  const response = await fetch("/__/firebase/init.json");
  if (!response.ok) throw new Error("Configuração do Firebase indisponível (/__/firebase/init.json).");
  return (await response.json()) as FirebaseOptions;
}

export async function initFirebase(): Promise<Services> {
  if (services) return services;
  const app = initializeApp(await loadOptions());
  const auth = getAuth(app);
  auth.languageCode = "pt-BR";
  const db = getFirestore(app);
  const functions = getFunctions(app, FUNCTIONS_REGION);
  if (usingEmulators) {
    connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  }
  services = { app, auth, db, functions };
  return services;
}

export function firebase(): Services {
  if (!services) throw new Error("initFirebase() must finish before the app renders.");
  return services;
}
